import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Briefcase, Inbox, Sparkles, IndianRupee, Users, ChevronDown, ChevronUp, X, ClipboardCheck, RotateCw, Loader2, BarChart2, ClipboardList, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { DeliverableTracker } from '../../components/DeliverableTracker';
import { CampaignProgressTab } from '../../components/brand/CampaignProgressTab';
import { BrandCampaignReportPanel } from '../../components/brand/BrandCampaignReportPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusMeta = {
  submitted: { label: 'Under Review', color: '#FFB800' },
  under_review: { label: 'Under Review', color: '#FFB800' },
  assigned: { label: 'Assigned', color: '#0055FF' },
  in_progress: { label: 'In Progress', color: '#0055FF' },
  completed: { label: 'Completed', color: '#00C972' },
  cancelled: { label: 'Cancelled', color: '#FF3B30' },
};

export const BrandCampaigns = () => {
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [expandedChild, setExpandedChild] = useState(null); // child campaign id
  // Per-campaign active tab — 'overview' (default) | 'progress'
  const [activeTab, setActiveTab] = useState({});

  // Reactivation modal state — {id, name} while asking; null when closed
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  const setTab = (cid, tab) => setActiveTab((p) => ({ ...p, [cid]: tab }));

  const handleReactivate = async () => {
    if (!reactivateTarget || reactivating) return;
    setReactivating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/brand/campaigns/${reactivateTarget.id}/reactivate`,
        {},
        { withCredentials: true },
      );
      toast.success(`Reactivated → "${data.campaign_name}"`);
      setReactivateTarget(null);
      // Refresh list, then expand the new campaign so the brand sees it immediately.
      await fetchCampaigns();
      setExpanded(data.id);
      // Optional: scroll to top of list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reactivation failed');
    } finally {
      setReactivating(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/brand/campaigns`, { withCredentials: true });
      setCampaigns(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Allows the sidebar count + Approvals Inbox to refresh instantly
  // whenever the brand acts inside DeliverableTracker.
  const handleTrackerChange = () => {
    fetchCampaigns();
    window.dispatchEvent(new Event('brand:approvals-refresh'));
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Deep-link:
  //   /brand/campaigns?focus=<deliverable_id>&bc=<brand_campaign_id>&child=<child_campaign_id>
  //     → deliverable-level drill from Approvals inbox
  //   /brand/campaigns?focus=<brand_campaign_id>
  //     → campaign-level drill from Agency Dashboard (Fix 8)
  useEffect(() => {
    if (loading || campaigns.length === 0) return;
    const params = new URLSearchParams(location.search);
    const focusVal = params.get('focus');
    const focusBc = params.get('bc');
    const focusChild = params.get('child');
    if (!focusVal) return;

    // Case A — deliverable focus (has bc + child qs). Retain old behaviour.
    if (focusBc && focusChild) {
      setExpanded(focusBc);
      setExpandedChild(focusChild);
      const t = setTimeout(() => {
        const rows = document.querySelectorAll('[data-testid="deliverable-row"]');
        for (const row of rows) {
          if (row.getAttribute('data-deliv-id') === focusVal) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            row.classList.add('ring-2', 'ring-[#FF451A]/40');
            setTimeout(() => row.classList.remove('ring-2', 'ring-[#FF451A]/40'), 2500);
            break;
          }
        }
      }, 1500);
      return () => clearTimeout(t);
    }

    // Case B — campaign focus (Agency Dashboard deep-link). Just expand.
    if (campaigns.some((c) => c.id === focusVal)) {
      setExpanded(focusVal);
      const t = setTimeout(() => {
        const card = document.querySelector(`[data-brand-campaign-id="${focusVal}"]`);
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [loading, campaigns, location.search]);

  const handleCancel = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Cancel this campaign brief? You can submit a new one anytime.')) return;
    try {
      await axios.post(`${API_URL}/api/brand/campaigns/${id}/cancel`, {}, { withCredentials: true });
      toast.success('Campaign cancelled');
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-campaigns-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2">My Campaigns</h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Track briefs submitted to StrawHat HQ</p>
            </div>
            <Link
              to="/brand/discover"
              className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17] transition-colors flex items-center gap-2"
            >
              <Sparkles size={16} /> New Campaign
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center">
              <Inbox className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={40} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No campaigns yet</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-5">Start by discovering creators and submitting your first brief.</p>
              <Link
                to="/brand/discover"
                className="inline-flex items-center gap-2 bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17] transition-colors"
              >
                <Sparkles size={16} /> Discover Creators
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {campaigns.map((c) => {
                const meta = statusMeta[c.status] || statusMeta.submitted;
                const isOpen = expanded === c.id;
                const canCancel = ['submitted', 'under_review'].includes(c.status);
                return (
                  <div key={c.id} data-brand-campaign-id={c.id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] overflow-hidden" data-testid="brand-campaign-card">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setExpanded(isOpen ? null : c.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpanded(isOpen ? null : c.id);
                        }
                      }}
                      className="w-full p-5 flex items-center gap-4 hover:bg-white/[0.03] transition-colors text-left cursor-pointer"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#FF451A]/10 flex items-center justify-center text-[#FF451A]">
                        <Briefcase size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#0F0F0F] dark:text-white text-lg">{c.campaign_name}</div>
                        <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1"><Users size={11} /> {c.creator_count}</span>
                          <span className="flex items-center gap-1"><IndianRupee size={11} />{(c.budget || 0).toLocaleString()}</span>
                          <span>{(c.platforms || []).join(', ')}</span>
                        </div>
                      </div>
                      <span
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                      {c.google_sheet?.client_url && (
                        <a
                          href={c.google_sheet.client_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="ml-1 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1 bg-[#0F9D58]/10 text-[#0F9D58] border border-[#0F9D58]/30 hover:bg-[#0F9D58]/20 transition-colors"
                          data-testid={`brand-client-sheet-btn-${c.id}`}
                          title="Open the live campaign sheet"
                        >
                          <ExternalLink size={11} /> Client Sheet
                        </a>
                      )}
                      {c.status === 'completed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(c.id);
                            setTab(c.id, 'report');
                          }}
                          className="ml-1 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1 bg-[#00C972]/10 text-[#00C972] border border-[#00C972]/30 hover:bg-[#00C972]/20 transition-colors"
                          data-testid={`view-report-btn-${c.id}`}
                          title="Download PDF/Excel and share the report"
                        >
                          <BarChart2 size={11} /> View Report
                        </button>
                      )}
                      {c.status === 'completed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setReactivateTarget({ id: c.id, name: c.campaign_name });
                          }}
                          className="ml-1 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1 bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/30 hover:bg-[#FF451A]/20 transition-colors"
                          data-testid={`run-again-btn-${c.id}`}
                          title="Run this campaign again with the same brief and creators"
                        >
                          <RotateCw size={11} /> Run Again
                        </button>
                      )}
                      {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>

                    {isOpen && (
                      <div className="p-5 pt-0 border-t border-black/[0.08] dark:border-white/5 bg-[#050505]/40 mt-2">
                        {/* Tab strip — Overview · Creator Progress · Mandates · Report */}
                        <div className="flex gap-1 pt-3 mb-4 border-b border-black/[0.08] dark:border-white/5 flex-wrap" data-testid="brand-campaign-tabs">
                          {[
                            { key: 'overview',  label: 'Overview' },
                            { key: 'progress',  label: 'Creator Progress' },
                            { key: 'mandates',  label: 'Mandates' },
                            ...(c.status === 'completed' ? [{ key: 'report', label: 'Report' }] : []),
                          ].map((t) => {
                            const active = (activeTab[c.id] || 'overview') === t.key;
                            return (
                              <button
                                key={t.key}
                                onClick={(e) => { e.stopPropagation(); setTab(c.id, t.key); }}
                                className={`px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors border-b-2 -mb-px ${active ? 'border-[#FF451A] text-[#0F0F0F] dark:text-white' : 'border-transparent text-[#6B6B6B] dark:text-zinc-500 hover:text-zinc-300'}`}
                                data-testid={`brand-campaign-tab-${t.key}`}
                              >
                                {t.label}
                              </button>
                            );
                          })}
                        </div>

                        {(activeTab[c.id] || 'overview') === 'progress' ? (
                          <CampaignProgressTab campaignId={c.id} />
                        ) : (activeTab[c.id] || 'overview') === 'mandates' ? (
                          <BrandMandatesTab campaignId={c.id} />
                        ) : (activeTab[c.id] || 'overview') === 'report' ? (
                          <BrandCampaignReportPanel campaignId={c.id} />
                        ) : (
                        <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                          <div>
                            <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-1">Brief</div>
                            <p className="text-sm text-[#0F0F0F] dark:text-white">{c.brief}</p>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-1">Categories</div>
                              <div className="flex flex-wrap gap-1.5">
                                {(c.categories || []).map((cat) => (
                                  <span key={cat} className="px-2 py-0.5 text-xs rounded-full bg-[#FF451A]/10 text-[#FF451A]">{cat}</span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-1">Deliverables</div>
                              <ul className="text-sm text-[#0F0F0F] dark:text-white list-disc list-inside">
                                {(c.deliverables || []).map((d, i) => <li key={i}>{d}</li>)}
                              </ul>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-1">Shortlisted</div>
                              <div className="text-sm text-[#0F0F0F] dark:text-white">{(c.shortlisted_creator_ids || []).length} creators</div>
                            </div>
                            {(c.assignments || []).length > 0 && (
                              <div>
                                <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-1">Assigned Managers</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {c.assignments.map((a, i) => (
                                    <span key={i} className="px-2 py-1 text-xs rounded-full bg-[#0055FF]/10 text-[#0055FF]">
                                      {a.manager_name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Per-creator live progress */}
                        {(c.child_progress || []).length > 0 && (
                          <div className="mt-5 pt-5 border-t border-black/[0.08] dark:border-white/5">
                            <div className="flex items-baseline justify-between mb-3">
                              <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400">Per-Creator Progress</div>
                              <div className="text-xs text-[#6B6B6B] dark:text-zinc-400">
                                <span className="font-semibold text-[#0F0F0F] dark:text-white">{c.completed_count || 0}</span>/{c.total_count || 0} completed
                              </div>
                            </div>
                            <div className="w-full h-1.5 bg-white dark:bg-[#0A0B0D] rounded-full overflow-hidden mb-3">
                              <div
                                className="h-full bg-[#00C972] rounded-full transition-all"
                                style={{ width: `${c.total_count ? Math.round((c.completed_count / c.total_count) * 100) : 0}%` }}
                              ></div>
                            </div>
                            <div className="space-y-1.5">
                              {c.child_progress.map((cp, i) => {
                                const stageColor = { pitched: '#0055FF', outreach: '#9B6EFF', locked: '#00C972', live: '#FF451A', completed: '#666666', declined: '#FF3B30' }[cp.stage] || '#999';
                                const fmt = (v) => `₹${Number(v).toLocaleString('en-IN')}`;
                                const hasNego = cp.negotiation_round_count > 0 || cp.latest_counter_offer != null || cp.finalized_rate != null;
                                const childKey = cp.campaign_id;
                                const childOpen = expandedChild === childKey;
                                const canReview = cp.campaign_id && ['locked', 'live', 'completed'].includes(cp.stage);
                                return (
                                  <div key={i} className="p-2 bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg" data-testid="brand-child-progress-row">
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-xs font-bold shrink-0">
                                          {cp.creator_name?.[0]?.toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                          <div className="text-sm font-medium text-[#0F0F0F] dark:text-white truncate">{cp.creator_name}</div>
                                          <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500">via {cp.manager_name}</div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <span
                                          className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                                          style={{ backgroundColor: `${stageColor}15`, color: stageColor }}
                                        >
                                          {cp.stage.replace('_', ' ')}
                                        </span>
                                        {canReview && (
                                          <button
                                            onClick={() => setExpandedChild(childOpen ? null : childKey)}
                                            className={`flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border transition-colors ${childOpen ? 'bg-[#FF451A]/10 border-[#FF451A]/30 text-[#FF451A]' : 'bg-white/5 border-black/[0.10] dark:border-white/10 text-zinc-300 hover:text-white hover:border-white/20'}`}
                                            data-testid="brand-review-deliverables-button"
                                          >
                                            <ClipboardCheck size={11} />
                                            {childOpen ? 'Hide' : 'Review'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {hasNego && (
                                      <div className="mt-2 pl-9 flex flex-wrap items-center gap-1.5" data-testid="brand-negotiation-chips">
                                        {cp.latest_brand_offer != null && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border border-black/[0.10] dark:border-white/10">
                                            offer {fmt(cp.latest_brand_offer)}
                                          </span>
                                        )}
                                        {cp.latest_counter_offer != null && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/20">
                                            counter {fmt(cp.latest_counter_offer)}
                                          </span>
                                        )}
                                        {cp.finalized_rate != null && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-[#00C972]/10 text-[#00C972] border border-[#00C972]/20 font-semibold">
                                            final {fmt(cp.finalized_rate)}
                                          </span>
                                        )}
                                        {cp.negotiation_round_count > 0 && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-white/5 text-[#6B6B6B] dark:text-zinc-500 border border-black/[0.10] dark:border-white/10">
                                            {cp.negotiation_round_count} round{cp.negotiation_round_count > 1 ? 's' : ''}
                                          </span>
                                        )}
                                        {cp.last_negotiation_by && (
                                          <span className="text-[10px] font-mono text-[#A1A1AA] dark:text-zinc-600">· by {cp.last_negotiation_by}</span>
                                        )}
                                      </div>
                                    )}
                                    {childOpen && canReview && (
                                      <div className="mt-3 pt-3 border-t border-black/[0.08] dark:border-white/5">
                                        <DeliverableTracker
                                          campaignId={childKey}
                                          creatorName={cp.creator_name}
                                          canCreate={false}
                                          focusApprovals={false}
                                          onChange={handleTrackerChange}
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {canCancel && (
                          <div className="mt-4 pt-4 border-t border-black/[0.08] dark:border-white/5 flex justify-end">
                            <Button
                              onClick={(e) => handleCancel(c.id, e)}
                              className="border border-[#FF3B30]/20 bg-transparent text-[#FF3B30] hover:bg-[#FF3B30]/5 rounded-full px-5 py-2 text-sm font-medium"
                              data-testid="brand-cancel-button"
                            >
                              <X size={14} className="mr-1.5" /> Cancel Brief
                            </Button>
                          </div>
                        )}
                        </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Reactivation confirmation modal (rendered outside the list so it
          survives the map re-renders and never causes scope issues). */}
      <Dialog open={!!reactivateTarget} onOpenChange={(o) => !o && setReactivateTarget(null)}>
        <DialogContent className="max-w-md bg-white dark:bg-[#0A0A0A] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white" data-testid="reactivate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCw size={16} className="text-[#FF451A]" />
              Run this campaign again?
            </DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-[#4B5563] dark:text-zinc-300 space-y-3">
            {reactivateTarget && (
              <div className="text-xs font-mono text-[#6B6B6B] dark:text-zinc-500 uppercase tracking-wider">
                {reactivateTarget.name}
              </div>
            )}
            <p>
              This will create a new campaign with the same creators and brief.
              Deliverables, addresses and metrics will start fresh.
              Continue?
            </p>
          </div>
          <DialogFooter className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setReactivateTarget(null)}
              disabled={reactivating}
              className="bg-white/5 border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white hover:bg-white/10 text-xs"
              data-testid="reactivate-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReactivate}
              disabled={reactivating}
              className="bg-[#FF451A] hover:bg-[#E63E17] text-white text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="reactivate-confirm-btn"
            >
              {reactivating ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
              Yes, run again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Brand-facing mandates viewer. Read-only, hides internal fields
// (assigned_by, notes, cost-related mandates).
const BrandMandatesTab = ({ campaignId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API_URL}/api/brand/campaigns/${campaignId}/mandates`,
          { withCredentials: true },
        );
        if (!cancelled) setItems(Array.isArray(data) ? data : (data?.mandates || []));
      } catch (_e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId]);

  if (loading) {
    return (
      <div className="py-6 flex items-center justify-center text-[#6B6B6B] dark:text-zinc-400 text-sm">
        <Loader2 size={14} className="animate-spin mr-2" /> Loading mandates…
      </div>
    );
  }

  const visible = (items || []).filter((m) => {
    // Hide cost-related mandates from the brand view — those are internal.
    const t = String(m.mandate_type || m.type || '').toLowerCase();
    return !(t.includes('cost') || t.includes('rate') || t.includes('margin'));
  });

  if (visible.length === 0) {
    return (
      <div className="border border-dashed border-black/[0.10] dark:border-white/10 rounded-2xl p-8 text-center text-sm text-[#6B6B6B] dark:text-zinc-400" data-testid="brand-mandates-empty">
        <ClipboardList size={24} className="mx-auto mb-2 opacity-50" />
        No mandates on this campaign yet.
      </div>
    );
  }

  const totalCreators = visible.reduce((mx, m) => {
    const c = Array.isArray(m.assigned_to) ? m.assigned_to.length : 0;
    return Math.max(mx, c);
  }, 0);

  return (
    <div className="space-y-3" data-testid="brand-mandates-tab">
      <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono">
        <span className="text-[#FF451A]">{visible.length}</span> mandate{visible.length === 1 ? '' : 's'} defined for this campaign
      </p>
      {visible.map((m, i) => {
        const assignedAll = m.assigned_to === 'all';
        const assignedCount = assignedAll ? totalCreators : (Array.isArray(m.assigned_to) ? m.assigned_to.length : 0);
        const created = m.created_at ? String(m.created_at).slice(0, 10) : null;
        const typeLabel = (m.mandate_type || m.type || 'mandate').replace(/_/g, ' ');
        return (
          <div
            key={m.mandate_id || m.id || i}
            className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-4"
            data-testid={`brand-mandate-card-${m.mandate_id || i}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-mono bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/25">
                    {typeLabel}
                  </span>
                  {created && (
                    <span className="text-[10px] font-mono text-[#6B6B6B] dark:text-zinc-500">
                      Added {created}
                    </span>
                  )}
                </div>
                <h4 className="text-sm font-semibold text-[#0F0F0F] dark:text-white leading-snug">
                  {m.title || m.name || 'Untitled mandate'}
                </h4>
              </div>
              <span
                className="px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-mono bg-[#0055FF]/10 text-[#0055FF] border border-[#0055FF]/25 inline-flex items-center gap-1 shrink-0"
                data-testid="brand-mandate-scope-chip"
              >
                <Users size={9} /> {assignedAll ? 'All creators' : `${assignedCount} creator${assignedCount === 1 ? '' : 's'}`}
              </span>
            </div>
            {(m.description || m.content) && (
              <p className="text-sm text-[#0F0F0F] dark:text-white/90 leading-relaxed whitespace-pre-line bg-zinc-50 dark:bg-white/[0.03] border border-black/[0.05] dark:border-white/5 rounded-xl p-3 mt-1">
                {m.description || m.content}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
